import React from "react";

import { Button, Stack, Grid, Typography, Box, Container, Divider, Link, styled } from "@mui/material";
import DashboardLayout from "../../../Layouts/Dashboard/dashboard";
import PushContentSlider from "../../../Components/Sliders/PushContentSlider";
import MovieSlider from "../../../Components/Sliders/MovieSlider";
import CollectionSlider from "../../../Components/Sliders/CollectionSlider";

import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";

import { routePaths } from "../../../Router/router";
import { Link as RouterLink, useNavigate } from "react-router";

import AutocompleteSearch from "../../../Components/Search/AutocompleteSearch/autocomplete-search";
import { MovieCardProps } from "../../../Components/Cards/MovieCard/movie-card";
import HomeHeroBackground from "../../../Components/Heros/HomeHeroBackground";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import GridViewOutlinedIcon from "@mui/icons-material/GridViewOutlined";
import { assetService, cacheService, rightService, thumbnailService } from "../../../Core/Api/services";
import { ThumbnailSize } from "../../../Core/Api/Thumbnail/thumbnail-api-service";
import { DbObjectCollectionInfos } from "../../../Core/Api/Types/db-object-collection-infos";
import { DbUserInfos } from "../../../Core/Api/Types/db-user-infos";
import { ObjectLayer } from "../../../Core/Api/Types/object-layer";
import { GetTitleRouterPath, mapTitleObjectToMovieCard, UserHasAcl, UserHasLicence } from "../../../Utils/helpers";
import { AclTypes } from "../../../Core/Api/Types/acl-types";
import { CollectionLocation } from "../../../Core/Api/Types/db-collection-location";
import { PlatformMode } from "../../../Core/Api/Types/platform-mode";
import { Licences } from "../../../Core/Api/Types/licences";
import { useTranslation } from "react-i18next";

export const getRandomMovieCover = () => `/static/images/temp/movie-cover-${Math.floor(Math.random() * 3) + 1}.jpg`;

interface publishedCollection {
    id: number;
    name: string;
    description: string;
    titles: Omit<MovieCardProps, "index">[];
}

function CatalogIndex() {
    const $dashboardRef = React.useRef<HTMLDivElement>(null);
    return (
        <DashboardLayout ref={$dashboardRef} showTopbarSearchOnScroll>
            <CatalogIndexContent $dashboardRef={$dashboardRef} />
        </DashboardLayout>
    );
}

const CatalogIndexContent: React.FC<{ $dashboardRef: React.RefObject<HTMLDivElement | null> }> = ({
    $dashboardRef,
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const mode = cacheService.GetPlatformMode();

    const maxContractItems = 10;

    const [isLoadingSlider, setIsLoadingSlider] = React.useState<boolean>(true);
    const [isLoadingRights, setIsLoadingRights] = React.useState<boolean>(true);
    const [isLoadingNewIn, setIsLoadingNewIn] = React.useState<boolean>(true);
    const [isLoadingPublishedCollections, setIsLoadingPublishedCollections] = React.useState<boolean>(true);
    const [wishlistData, setWishlistData] = React.useState<DbObjectCollectionInfos[]>([]);
    const [isNewPlatform, setIsNewPlatform] = React.useState<boolean>(false);

    const { id: userId, groupId } = { ...(cacheService.GetUserInfo() as DbUserInfos) };
    const metadataFields = cacheService.GetMetadata();
    const user = cacheService.GetUserInfo();

    const [titlesLatestRights, setTitlesLatestRights] = React.useState<Omit<MovieCardProps, "index">[]>([]);
    const [titlesLatestCatalog, setTitlesLatestCatalog] = React.useState<Omit<MovieCardProps, "index">[]>([]);
    const [storeFrontTitles, setStoreFrontTitles] = React.useState<Omit<MovieCardProps, "index">[]>([]);
    const [publishedCollectionsTitles, setPublishedCollectionsTitles] = React.useState<publishedCollection[]>([]);

    const loadLatestTitles = () => {
        assetService.ListLastObjects(ObjectLayer.Title, maxContractItems).then((response) => {
            if (response.results) {
                const movieCardsDataCatalog: Omit<MovieCardProps, "index">[] = response
                    .results!.reverse()
                    .map((title) => mapTitleObjectToMovieCard(title!, metadataFields));

                setTitlesLatestCatalog(movieCardsDataCatalog);
            }

            setIsLoadingNewIn(false);
        });
    };

    // Load latest title from rights and catalog
    React.useEffect(() => {
        if (mode !== PlatformMode.Distribution) {
            navigate(routePaths.drive.path);
            return;
        }

        assetService.GetCollectionStoreFront().then((data) => {
            let storeFront = data
                .filter((x) => x.location === CollectionLocation.Carousel)
                .find((x) => x.targetGroupIds?.includes(groupId!));

            let publishedCollections = data.filter(
                (x) =>
                    x.location !== CollectionLocation.Carousel &&
                    x.isPublished &&
                    (x.targetGroupIds?.includes(groupId!) || x.creationInfos?.userId === userId)
            );

            if (publishedCollections)
                publishedCollections.forEach((collection) => {
                    if (collection.objectInfos && collection.objectInfos.length > 0) {
                        let movieCardsPublishedCollectionTitles: Omit<MovieCardProps, "index">[] = [];

                        if (collection.randomDisplay) {
                            movieCardsPublishedCollectionTitles = collection!.objectInfos
                                .sort(() => Math.random() - 0.5)
                                .map((title) => mapTitleObjectToMovieCard(title!, metadataFields));
                        } else {
                            movieCardsPublishedCollectionTitles = collection
                                .objectIds!.map((id) => collection!.objectInfos?.find((x) => x.id === id))
                                .reverse()
                                .map((title) => mapTitleObjectToMovieCard(title!, metadataFields));
                        }

                        let publishedBloc: publishedCollection = {
                            id: collection.id,
                            name: collection.name || "",
                            description: collection.description || "",
                            titles: movieCardsPublishedCollectionTitles,
                        };

                        setPublishedCollectionsTitles((current) => [...current, publishedBloc]);
                    }
                    setIsLoadingPublishedCollections(false);
                });

            if (storeFront && storeFront.objectInfos && storeFront.objectInfos.length > 0) {
                let movieCardsStoreFrontTitles: Omit<MovieCardProps, "index">[] = [];
                if (storeFront.randomDisplay) {
                    movieCardsStoreFrontTitles = storeFront!.objectInfos
                        .sort(() => Math.random() - 0.5)
                        .map((title) => mapTitleObjectToMovieCard(title!, metadataFields));
                } else {
                    movieCardsStoreFrontTitles = storeFront
                        .objectIds!.map((id) => storeFront!.objectInfos?.find((x) => x.id === id))
                        .reverse()
                        .map((title) => mapTitleObjectToMovieCard(title!, metadataFields));
                }

                setStoreFrontTitles(movieCardsStoreFrontTitles);
            }
        });

        if (UserHasAcl(AclTypes.aclRightsView)) {
            rightService.GetRightContracts(maxContractItems, metadataFields, true).then((response) => {
                const movieCardsDataRights: Omit<MovieCardProps, "index">[] = response
                    .reverse()
                    .map((title) => mapTitleObjectToMovieCard(title.titleInfos!, metadataFields));

                setTitlesLatestRights(movieCardsDataRights);
                setIsLoadingRights(false);
            });
        } else setIsLoadingRights(false);

        loadLatestTitles();
    }, [mode]);

    // Load collections
    React.useEffect(() => {
        assetService.GetCollectionWishList().then((data) => setWishlistData(data));
    }, []);

    const movieNewInCardsData: Omit<MovieCardProps, "index">[] = React.useMemo(() => {
        /*if (titlesLatestRights.length === 0)
          return titlesLatestCatalog.map((movie) => ({
            ...movie,
            cover: movie.cover,
          }));
        else
          return titlesLatestRights.map((movie) => ({
            ...movie,
            cover: movie.cover,
          }));*/

        return titlesLatestCatalog.map((movie) => ({
            ...movie,
            cover: movie.cover,
        }));
    }, [titlesLatestRights, titlesLatestCatalog]);

    const collectionListData = React.useMemo(
        () => ({
            list: wishlistData
                .filter((collection) => {
                    return (
                        (!collection.targetGroupIds?.includes(groupId!) && collection.targetUserIds?.includes(userId!)) ?? true
                    );
                })
                .map((collection) => {
                    const covers =
                        collection?.objectIds != undefined && collection?.objectIds?.length > 4
                            ? collection.objectIds
                                ?.slice(0, -10)
                                .map<string>((objectId) => thumbnailService.GetObjectPosterUrl(objectId, ThumbnailSize.small))
                            : collection.objectIds?.map<string>((objectId) =>
                                thumbnailService.GetObjectPosterUrl(objectId, ThumbnailSize.small)
                            ) || [];

                    return {
                        isCreationCard: false,
                        id: collection.id,
                        title: collection.name!,
                        nbTitles: collection.objectInfos?.filter((x) => x.layer === ObjectLayer.Title).length || 0,
                        nbTotaleItem: collection.objectIds?.length || 0,
                        covers: [covers[0] || "", covers[1] || "", covers[2] || "", covers[3] || ""],
                        selectMode: false,
                        creationInfos: collection.creationInfos,
                    };
                }),
        }),
        [wishlistData]
    );

    // Slider data
    const pushContentData = React.useMemo(() => {
        if (storeFrontTitles.length > 0) {
            return storeFrontTitles.map((push, index) => ({
                to: `${GetTitleRouterPath()}/${push.id}`,
                cover: thumbnailService.GetObjectPosterUrl(push.id, ThumbnailSize.xxlarge),
                casting: push.expand.quality,
                tags: [push.transparentChipText ?? "", ...push.labelList],
                id: push.id,
                title: push.title,
            }));
        } else if (titlesLatestRights.length === 0) {
            return titlesLatestCatalog.map((push, index) => ({
                to: `${GetTitleRouterPath()}/${push.id}`,
                cover: thumbnailService.GetObjectPosterUrl(push.id, ThumbnailSize.xxlarge),
                casting: push.expand.quality,
                tags: [push.transparentChipText ?? "", ...push.labelList],
                id: push.id,
                title: push.title,
            }));
        } else
            return titlesLatestRights.map((push, index) => ({
                to: `${GetTitleRouterPath()}/${push.id}`,
                cover: thumbnailService.GetObjectPosterUrl(push.id, ThumbnailSize.xxlarge),
                casting: push.expand.quality,
                tags: [push.transparentChipText ?? "", ...push.labelList],
                id: push.id,
                title: push.title,
            }));
    }, [titlesLatestRights, titlesLatestCatalog, storeFrontTitles]);

    // Slider covers to get background main colors
    const covers = React.useMemo(() => {
        return pushContentData.map((movie) => movie.cover);
    }, [pushContentData]);

    React.useEffect(() => {
        if (!isLoadingNewIn && !isLoadingRights) setIsLoadingSlider(false);
    }, [isLoadingNewIn, isLoadingRights]);

    const [activePushContentIndex, setActivePushContentIndex] = React.useState(0);

    const onChangePushContentSlide = React.useCallback((activeSlideIndex: number) => {
        setActivePushContentIndex(activeSlideIndex);
    }, []);

    React.useEffect(() => {
        if (!isLoadingSlider && pushContentData.length === 0) setIsNewPlatform(true);
    }, [isLoadingSlider, pushContentData.length]);

    const homePageMemo = React.useMemo(() => {
        return (
            <>
                <Box sx={{ position: "fixed", left: 0, right: 0, top: 0, bottom: 0, pointerEvents: "none" }}>
                    <HomeHeroBackground covers={covers} visibleCoverIndex={activePushContentIndex} />
                </Box>
                <Container sx={{ mt: 8, mb: 6, position: "relative" }}>
                    <StyledTitle variant="h1" fontWeight={700} textAlign="center">
                        {!isNewPlatform ? cacheService.GetCatchPhrase() : t("page:body.welcomeToYourPlatform")}
                    </StyledTitle>
                    <Grid container justifyContent="center" sx={{ mb: 6, mt: 1 }}>
                        <Grid item md={6}>
                            <AutocompleteSearch />
                            <Stack direction="row" justifyContent="center" alignItems="center" sx={{ mt: 3 }}>
                                <Button
                                    component={RouterLink}
                                    to={`${routePaths.catalog.path}/movies/all`}
                                    variant="rounded"
                                    size="xlarge"
                                >
                                    {t("common:movies")}
                                </Button>
                                <Divider orientation="vertical" variant="middle" flexItem sx={{ height: 12, m: "auto 0" }} />
                                <Button
                                    component={RouterLink}
                                    to={`${routePaths.catalog.path}/series/all`}
                                    variant="rounded"
                                    size="xlarge"
                                >
                                    {t("button:series")}
                                </Button>
                                <Divider orientation="vertical" variant="middle" flexItem sx={{ height: 12, m: "auto 0" }} />
                                <Button component={RouterLink} to={`${routePaths.collections.path}`} variant="rounded" size="xlarge">
                                    {t("common:collections")}
                                </Button>
                                {user && user.customerIds && user.customerIds.length > 0 && (
                                    <>
                                        <Divider orientation="vertical" variant="middle" flexItem sx={{ height: 12, m: "auto 0" }} />

                                        <Button
                                            component={RouterLink}
                                            to={`${routePaths.catalog.path}/find/*`}
                                            variant="rounded"
                                            size="xlarge"
                                            startIcon={<GridViewOutlinedIcon />}
                                        >
                                            {t("button:browseAll")}
                                        </Button>
                                    </>
                                )}
                            </Stack>
                        </Grid>
                    </Grid>

                    {isNewPlatform && UserHasAcl(AclTypes.aclTitleCreate) && UserHasLicence(Licences.CatalogManagement) && (
                        <Stack mt={4} spacing={1} alignContent="center" textAlign="center" alignItems="center">
                            <Typography component="h2" variant="h3">
                                {t("page:body.createFirstTitleToStart")}
                            </Typography>
                            <Button
                                onClick={() => navigate(routePaths.inventoryCreateTitle.path)}
                                variant="contained"
                                fullWidth={false}
                                startIcon={<AddCircleOutlineIcon />}
                            >
                                {t("button:createATitle")}
                            </Button>
                        </Stack>
                    )}

                    {!isNewPlatform && (
                        <PushContentSlider
                            list={pushContentData}
                            onChangeSlide={onChangePushContentSlide}
                            loading={isLoadingSlider}
                        />
                    )}
                </Container>

                <Container sx={{ position: "relative" }}>
                    {(isLoadingNewIn || movieNewInCardsData.length > 0) && (
                        <Box component="section" sx={{ mt: 4, mb: 4 }}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                                <Typography component="h2" variant="h3">
                                    {t("page:body.newIn")}
                                </Typography>
                                <Link
                                    component={RouterLink}
                                    to={routePaths.catalog.path}
                                    variant="body1"
                                    color="white"
                                    underline="hover"
                                >
                                    {t("common:seeAll")}
                                    <KeyboardArrowRightIcon fontSize="small" />
                                </Link>
                            </Stack>
                            <MovieSlider list={movieNewInCardsData} loading={isLoadingNewIn} />
                        </Box>
                    )}

                    {(isLoadingRights || titlesLatestRights.length > 0) && (
                        <Box component="section" sx={{ mt: 4, mb: 5 }}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                                <Typography component="h2" variant="h3">
                                    {t("page:body.myActiveTitles")}
                                </Typography>
                                <Link
                                    component={RouterLink}
                                    to={routePaths.titleContracts.path}
                                    variant="body1"
                                    color="white"
                                    underline="hover"
                                >
                                    {t("common:seeAll")}
                                    <KeyboardArrowRightIcon fontSize="small" />
                                </Link>
                            </Stack>
                            <MovieSlider list={titlesLatestRights} loading={isLoadingRights} />
                        </Box>
                    )}

                    {/*
          For now -> hide this block because we don't have algo or AI to determine top titles!
          <Box component="section">
            <Grid container columnSpacing={3} sx={{ mt: 3 }}>
              <Grid item md={4}>
                <Typography component="h2" variant="h3">
                  Top Titles
                </Typography>
              </Grid>
              <Grid item md={8} display="flex">
                <ButtonTabBar tabs={[{ label: "This week" }, { label: "This month" }, { label: "This year" }]} />
              </Grid>
            </Grid>
            <MovieList {...movieTopTitleData} sx={{ mt: 2, mb: 2 }} />
            <Stack
              justifyContent="center"
              alignItems="center"
              sx={{
                background:
                  "radial-gradient(46.77% 100% at 50% 100.96%, rgba(255, 255, 255, 0.015) 0%, rgba(255, 255, 255, 0) 100%)",
                pt: 2,
                pb: 6,
              }}
            >
              <Button size="large" sx={{ minWidth: 280 }}>
                View more
              </Button>
            </Stack>
            </Box>*/}

                    {collectionListData.list.length > 0 && (
                        <Box component="section" sx={{ mt: 4, mb: 2 }}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                                <Typography component="h2" variant="h3">
                                    {t("common:collections")}
                                </Typography>
                                <Link
                                    component={RouterLink}
                                    to={routePaths.collections.path}
                                    variant="body1"
                                    color="white"
                                    underline="hover"
                                >
                                    {t("common:seeAll")}
                                    <KeyboardArrowRightIcon fontSize="small" />
                                </Link>
                            </Stack>
                            <CollectionSlider
                                list={collectionListData.list.map((collection) => {
                                    return {
                                        to: routePaths.collections.path + "/" + collection.id,
                                        nbitem: collection.nbTotaleItem,
                                        id: collection.id,
                                        covers: collection.covers,
                                        title: collection.title,
                                    };
                                })}
                            />
                        </Box>
                    )}

                    {publishedCollectionsTitles.map((publishedCollection) => (
                        <Box
                            component="section"
                            key={publishedCollection.id}
                            sx={{
                                pt: 5,
                                pb: 5,
                                pl: 7,
                                pr: 3,
                                mr: -3,
                                ml: -3,
                                mb: 3,
                                backgroundColor: "background.paper",
                            }}
                        >
                            <Grid container justifyContent="space-between">
                                <Grid
                                    item
                                    md={3}
                                    display="flex"
                                    alignItems="flex-start"
                                    flexDirection="column"
                                    justifyContent="space-between"
                                >
                                    <Stack sx={{ mb: 2 }}>
                                        <Typography variant="h2">{publishedCollection.name}</Typography>
                                        <Typography variant="subtitle2" component="p" sx={{ mt: 3, opacity: 0.4 }}>
                                            {publishedCollection.description}
                                        </Typography>
                                    </Stack>
                                    <Button
                                        onClick={() => navigate(`${routePaths.storefront.path}/${publishedCollection.id}`)}
                                        variant="outlined"
                                        size="large"
                                    >
                                        {t("button:viewMore")}
                                    </Button>
                                </Grid>
                                <Grid item xs={12} md={8}>
                                    <MovieSlider
                                        list={publishedCollection.titles}
                                        background="paper"
                                        loading={isLoadingPublishedCollections}
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    ))}

                    {/* <Box component="section" sx={{ mt: 7, mb: 8 }}>
            <Typography component="h2" variant="h3" sx={{ mb: 2 }}>
              Explore by genre
            </Typography>
            <Grid container spacing={2}>
              {genreData.map((genre) => (
                <Grid key={genre.id} item>
                  <GenreCard {...genre} to={routePaths.catalog.path} />
                </Grid>
              ))}
            </Grid>
          </Box> */}
                </Container>
            </>
        );
    }, [
        collectionListData,
        activePushContentIndex,
        collectionListData,
        isLoadingSlider,
        isLoadingRights,
        isLoadingNewIn,
        pushContentData,
        cacheService.GetAuthKey(),
        isNewPlatform,
        titlesLatestRights,
        movieNewInCardsData,
    ]);

    return homePageMemo;
};

export const StyledTitle = styled(Typography)(({ theme }) => ({
    transition: "opacity .6s cubic-bezier(0.22, 1, 0.36, 1)",
    willChange: "opacity",
}));

export default CatalogIndex;
